import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SearchPopup.css';

// Sample search data - in a real app, this would come from an API
// API search function
const searchInAPI = async (query) => {
    try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
        if (!response.ok) {
            throw new Error('Search request failed');
        }
        const data = await response.json();
        return data.results || [];
    } catch (error) {
        console.error('Search API error:', error);
        return [];
    }
};

function SearchPopup({ isOpen, onClose }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const popupRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                onClose();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        if (searchTerm.length > 0) {
            setIsLoading(true);
            // Call API search
            const timeoutId = setTimeout(async () => {
                const results = await searchInAPI(searchTerm);
                setSearchResults(results);
                setIsLoading(false);
            }, 300);

            return () => clearTimeout(timeoutId);
        } else {
            setSearchResults([]);
            setIsLoading(false);
        }
    }, [searchTerm]);

    const handleResultClick = (result) => {
        onClose();
        setSearchTerm('');

        // Use the url field from API response for navigation
        if (result.url) {
            navigate(result.url);
        } else {
            // Fallback for old static data
            switch (result.type) {
                case 'specialty':
                    navigate(`/specialitati?search=${encodeURIComponent(result.title)}`);
                    break;
                case 'doctor':
                    navigate(`/medici?search=${encodeURIComponent(result.title)}`);
                    break;
                case 'service':
                    navigate(`/servicii?search=${encodeURIComponent(result.title)}`);
                    break;
                case 'article':
                    navigate(`/articole?search=${encodeURIComponent(result.title)}`);
                    break;
                default:
                    break;
            }
        }
    };

    const getResultIcon = (type) => {
        switch (type) {
            case 'specialty':
                return '🏥';
            case 'doctor':
                return '👨‍⚕️';
            case 'service':
                return '🔬';
            case 'article':
                return '📄';
            default:
                return '🔍';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="search-popup-overlay">
            <div className="search-popup" ref={popupRef}>
                <div className="search-popup-header">
                    <div className="search-input-container">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Caută specialități, medici, servicii..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        <button
                            className="search-close-btn"
                            onClick={onClose}
                            aria-label="Închide căutarea"
                        >
                            ✕
                        </button>
                    </div>
                </div>

                <div className="search-popup-content">
                    {isLoading && (
                        <div className="search-loading">
                            <div className="search-spinner"></div>
                            <span>Căutare...</span>
                        </div>
                    )}

                    {!isLoading && searchTerm.length === 0 && (
                        <div className="search-placeholder">
                            <div className="search-placeholder-icon">🔍</div>
                            <h3>Caută în Evoo Medical</h3>
                            <p>Specialități medicale, medici, servicii și mai mult</p>
                            <div className="search-suggestions">
                                <div className="suggestion-tag">Cardiologie</div>
                                <div className="suggestion-tag">Dermatologie</div>
                                <div className="suggestion-tag">Oftalmologie</div>
                                <div className="suggestion-tag">Dr. Maria Popescu</div>
                            </div>
                        </div>
                    )}

                    {!isLoading && searchTerm.length > 0 && searchResults.length === 0 && (
                        <div className="search-no-results">
                            <div className="no-results-icon">😔</div>
                            <h3>Niciun rezultat găsit</h3>
                            <p>Încearcă să cauți cu alte cuvinte</p>
                        </div>
                    )}

                    {!isLoading && searchResults.length > 0 && (
                        <div className="search-results">
                            {searchResults.map((result) => (
                                <div
                                    key={result.id}
                                    className="search-result-item"
                                    onClick={() => handleResultClick(result)}
                                >
                                    <div className="result-icon">
                                        {getResultIcon(result.type)}
                                    </div>
                                    <div className="result-content">
                                        <div className="result-title">{result.title}</div>
                                        <div className="result-description">{result.description}</div>
                                    </div>
                                    <div className="result-arrow">→</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SearchPopup;
